import { z } from 'zod';

/**
 * The per-tenant feature switches (design 2026-11-03).
 *
 * Six keys, one list: backend guard, services and every frontend gate import
 * THESE, so a seventh switch is added here and nowhere else. The two
 * organization flags are NOT part of it — they stay the columns
 * `canHaveAdditionalOrganizations` / `canHaveIndividualTrainers`.
 *
 * The order is the one the admin panel shows and `featuresOfRoute` returns.
 */
export const TENANT_FEATURE_KEYS = [
  'marketplace',
  'mfwContent',
  'premium',
  'sessions',
  'desk',
  'lockers',
] as const;

export type TenantFeatureKey = (typeof TENANT_FEATURE_KEYS)[number];

/**
 * What `TenantSettings.features` holds: SPARSE. Only a switched-off feature is
 * stored, as the literal `false`; a missing key, `{}` or a NULL column all mean
 * "on", so every tenant that existed before the column reads as all-on and
 * re-enabling a feature removes its key.
 */
export type TenantFeatures = Partial<Record<TenantFeatureKey, false>>;

/**
 * The stored shape as a schema. A native JSON object (not a JSON-encoded
 * string), exactly the six keys, `false` or absent — `{ marketplace: true }` is
 * refused because "on" is the absence of the key. Distinct from
 * `UpdateTenantFeaturesDto`, the full eight-boolean write body that is
 * normalized INTO this shape.
 */
export const StoredTenantFeatures = z
  .object(
    Object.fromEntries(
      TENANT_FEATURE_KEYS.map((key) => [key, z.literal(false).optional()]),
    ) as Record<TenantFeatureKey, z.ZodOptional<z.ZodLiteral<false>>>,
  )
  .strict()
  .nullable();

/**
 * Permissions that START something new in a switchable feature, mapped to the
 * switch they sit behind. Read by the backend guard (a mapped permission on a
 * route implies its feature) and by the frontend `hasPermission`.
 *
 * Deliberately NOT in the map, because they serve what already exists and the
 * switch lets that run out: `booking_events_read/update/delete`,
 * `bookings_read/cancel/manage`, and `bookings_waitlist` — one permission that
 * covers join/accept AND decline/leave, so only the join and accept routes
 * declare the feature explicitly. `desk` and `lockers` hide module surfaces;
 * their financial reports and settlement processes stay independent.
 */
export const TENANT_FEATURE_BY_PERMISSION: Readonly<
  Record<string, TenantFeatureKey>
> = Object.freeze({
  booking_event_templates_create: 'sessions',
  booking_event_templates_read: 'sessions',
  booking_event_templates_update: 'sessions',
  booking_event_templates_delete: 'sessions',
  // Also behind "assign template".
  booking_events_create: 'sessions',
  bookings_create: 'sessions',
  desk_products_manage: 'desk',
  desk_sales_manage: 'desk',
  locker_keys_manage: 'lockers',
});

/**
 * Off only for the literal `false`. A tenant with no stored value (NULL, `{}`,
 * a missing key, or a row not loaded yet) is on: the safe direction for a
 * switch that must change nothing at deploy.
 */
export const isTenantFeatureOn = (
  features: TenantFeatures | null | undefined,
  key: TenantFeatureKey,
): boolean => features?.[key] !== false;

/**
 * The write DTO's six switches as the sparse stored object. NULL when every
 * switch is on, so an all-on tenant is indistinguishable from one that never
 * had the column.
 */
export const toStoredTenantFeatures = (
  values: Record<TenantFeatureKey, boolean>,
): TenantFeatures | null => {
  const off = TENANT_FEATURE_KEYS.filter((key) => !values[key]);
  if (off.length === 0) return null;
  return Object.fromEntries(off.map((key) => [key, false])) as TenantFeatures;
};

/**
 * Every feature a route requires: the ones its permissions imply plus the ones
 * it declares itself (`@SecureApi({ tenantFeature })`). Deduplicated and in the
 * order of `TENANT_FEATURE_KEYS`. The caller requires ALL of them to be on —
 * AND, independent of the ANY semantics of a permission array.
 */
export const featuresOfRoute = (
  permissions: readonly string[] | undefined,
  declared: TenantFeatureKey | readonly TenantFeatureKey[] | undefined,
): TenantFeatureKey[] => {
  const required = new Set<TenantFeatureKey>();
  for (const permission of permissions ?? []) {
    // Own keys only: a permission string is not trusted to avoid `toString`.
    if (
      Object.prototype.hasOwnProperty.call(
        TENANT_FEATURE_BY_PERMISSION,
        permission,
      )
    ) {
      required.add(TENANT_FEATURE_BY_PERMISSION[permission]);
    }
  }
  if (typeof declared === 'string') required.add(declared);
  else for (const key of declared ?? []) required.add(key);
  return TENANT_FEATURE_KEYS.filter((key) => required.has(key));
};
